import { EDUCATORS_ROUTE_TERMS } from '~/constants/routes/educators';
import { UNIFIED_ROUTE_ACCEPT_TERMS, UNIFIED_ROUTE_AUTH } from '~/constants/routes/unified';
import { ClusterTermsRequirement } from '~/graphql/generated/graphql';
import { useConfigStore } from '~/stores/config';
import { AdminMfaRouteDecision, adminMfaRouteDecision } from '~/utils/adminMfaRoute';

const EDUCATOR_ACCEPT_TERMS_PATH = '/educators/accept-terms';
const STUDENT_ACCEPT_TERMS_PATH = UNIFIED_ROUTE_ACCEPT_TERMS;

// Login/verify flows must stay reachable — the gate only makes sense once a
// session exists — the interstitials themselves must never self-redirect, and
// the legal pages they link to must be readable before accepting. /start is
// the shared-lesson entry surface, open to guests resolving a code; an
// authenticated user with pending terms is caught on the next surface they
// navigate to, with the redirect carrying the lesson hand-off params along.
// The provider login page clears the last person's client state before anything
// else may redirect; the gate catches the new person on the landing it navigates
// to. The failure page must read on a shared device where the last person is
// still signed in with terms outstanding.
const EXEMPT_PATHS = [
  EDUCATOR_ACCEPT_TERMS_PATH,
  STUDENT_ACCEPT_TERMS_PATH,
  UNIFIED_ROUTE_AUTH,
  '/start',
  '/educators/login',
  '/educators/verify-email',
  EDUCATORS_ROUTE_TERMS,
  '/terms',
  '/privacy-policy',
];

/**
 * Cluster-terms gate. This route middleware is the enforcement: while the self
 * query carries a pending `user.clusterTermsRequirement`, every non-exempt
 * route redirects to the acceptance interstitial, carrying the intended path
 * to return to. The interstitials have no dismiss, so the user cannot get past
 * them without accepting. It also fires when `user.schoolsAwaitingTermsAcceptance`
 * names a school whose terms the student has not accepted yet.
 *
 * The requirement is decided once, at login, and stored on the session, so a
 * running session is never interrupted by a `requires_reacceptance` publish,
 * the consumer gate flag flipping on, a login on another device, or a role
 * change; the user is asked at their next login. The API deliberately has no
 * per-request terms gate. Do not re-add one: it would interrupt running
 * sessions mid-flow, which is exactly what this design avoids.
 *
 * An admin who still owes the second factor is left to the admin MFA gate (03a)
 * first, whose login page this gate would otherwise redirect away from; the
 * terms catch them on the next navigation.
 */
export default defineNuxtRouteMiddleware(async (to) => {
  const { user } = useAuth();
  const requirement = user.value?.clusterTermsRequirement;
  const clusterTermsDue = !!requirement && requirement !== ClusterTermsRequirement.None;
  // A school role written without an invite (a managed role from a sync run) skipped the
  // creation-time school terms gate; the same interstitial collects that consent.
  const schoolTermsDue = (user.value?.schoolsAwaitingTermsAcceptance ?? []).length > 0;
  if ((!clusterTermsDue && !schoolTermsDue) || adminMfaRouteDecision({ path: to.path, status: user.value?.adminMfaStatus }) !== AdminMfaRouteDecision.none) {
    return;
  }

  const path = clusterRelativePath(to.path);
  if (EXEMPT_PATHS.some(exempt => path === exempt || path.startsWith(`${exempt}/`))) {
    return;
  }

  // Mirrors ClusterTermsService::audienceFor — on schools clusters any teacher
  // role resolves to the educator document; everyone else accepts the student
  // one. Consumer clusters only gate students. Students must never be sent to
  // /educators/*, whose role guard would bounce them to the educator login.
  const isSchoolsCluster = useConfigStore().isSchoolsCluster;
  const interstitial = isSchoolsCluster && user.value?.teacherRoles?.length
    ? EDUCATOR_ACCEPT_TERMS_PATH
    : STUDENT_ACCEPT_TERMS_PATH;

  return await navigateTo(
    { path: clusterRedirectPath(interstitial), query: { redirect: clusterRelativePath(to.fullPath) } },
    { replace: true },
  );
});
